import { apiClient } from '../utils/api-client'
import type { TrainingPlan, TrainingPlanPayload } from '../types/training-plan'

interface TrainingPlanResponse {
  data: { id: string; type: string; attributes: TrainingPlan }
}

interface TrainingPlansResponse {
  data: Array<{ id: string; type: string; attributes: TrainingPlan }>
}

function unwrap(json: TrainingPlanResponse): TrainingPlan {
  return { ...json.data.attributes, id: json.data.id }
}

class TrainingPlansService {
  async list(): Promise<TrainingPlan[]> {
    const response = await apiClient.get('/api/training_plans', { requireAuth: true })
    const json: TrainingPlansResponse = await response.json()
    return json.data.map((d) => ({ ...d.attributes, id: d.id }))
  }

  async get(id: string): Promise<TrainingPlan> {
    const response = await apiClient.get(`/api/training_plans/${id}`, { requireAuth: true })
    return unwrap(await response.json())
  }

  async create(payload: TrainingPlanPayload): Promise<TrainingPlan> {
    const response = await apiClient.post(
      '/api/training_plans',
      { data: { attributes: payload } },
      { requireAuth: true }
    )
    return unwrap(await response.json())
  }

  async update(id: string, payload: TrainingPlanPayload): Promise<TrainingPlan> {
    const response = await apiClient.patch(
      `/api/training_plans/${id}`,
      { data: { attributes: payload } },
      { requireAuth: true }
    )
    return unwrap(await response.json())
  }

  async accept(id: string): Promise<TrainingPlan> {
    const response = await apiClient.post(
      `/api/training_plans/${id}/accept`,
      {},
      { requireAuth: true }
    )
    return unwrap(await response.json())
  }

  async reject(id: string): Promise<TrainingPlan> {
    const response = await apiClient.post(
      `/api/training_plans/${id}/reject`,
      {},
      { requireAuth: true }
    )
    return unwrap(await response.json())
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/api/training_plans/${id}`, { requireAuth: true })
  }
}

export const trainingPlansService = new TrainingPlansService()
